import { supabase } from '../lib/supabase';
import { Room, RoomState, BroadcastLink, RoomMember, RoomMemberRole, RoomInvitation } from '../types';

export class RoomService {
  static async getUserRooms(userId: string): Promise<Room[]> {
    try {
      // First get rooms where user is a member
      const { data: roomMemberships, error: membershipError } = await supabase
        .from('room_members')
        .select(`
          room_id,
          role,
          rooms!inner (
            id,
            name,
            size,
            view_mode,
            current_state_id,
            auto_reset_state_id,
            auto_reset_minutes,
            created_by,
            created_at,
            updated_at
          )
        `)
        .eq('user_id', userId);

      if (membershipError) {
        console.error('Error fetching room memberships:', membershipError);
        throw membershipError;
      }

      if (!roomMemberships || roomMemberships.length === 0) {
        return [];
      }

      const roomIds = roomMemberships.map((rm: any) => rm.room_id);

      // Get room states
      const { data: roomStates, error: statesError } = await supabase
        .from('room_states')
        .select('*')
        .in('room_id', roomIds);

      if (statesError) {
        console.error('Error fetching room states:', statesError);
        throw statesError;
      }

      // Get all room members with user details
      const { data: allRoomMembers, error: membersError } = await supabase
        .from('room_members')
        .select(`
          room_id,
          user_id,
          role,
          joined_at,
          users!inner (
            id,
            name,
            email,
            avatar
          )
        `)
        .in('room_id', roomIds);

      if (membersError) {
        console.error('Error fetching room members:', membersError);
        throw membersError;
      }

      // Get broadcast links
      const { data: broadcastLinks, error: linksError } = await supabase
        .from('broadcast_links')
        .select('*')
        .in('room_id', roomIds);

      if (linksError) {
        console.error('Error fetching broadcast links:', linksError);
        throw linksError;
      }

      // Transform the data into Room objects
      const rooms: Room[] = roomMemberships.map((membership: any) => {
        const room = membership.rooms;
        const states = roomStates?.filter((state: any) => state.room_id === room.id) || [];
        const members = allRoomMembers?.filter((member: any) => member.room_id === room.id) || [];
        const links = broadcastLinks?.filter((link: any) => link.room_id === room.id) || [];

        const currentState = states.find((state: any) => state.id === room.current_state_id) || states[0];
        const autoResetState = room.auto_reset_state_id 
          ? states.find((state: any) => state.id === room.auto_reset_state_id)
          : undefined;

        return {
          id: room.id,
          name: room.name,
          size: room.size as 'small' | 'medium' | 'large',
          viewMode: room.view_mode as 'minimal' | 'detailed',
          currentState: currentState ? {
            id: currentState.id,
            name: currentState.name,
            color: currentState.color,
            emoji: currentState.emoji
          } : {
            id: 'default',
            name: 'Unknown',
            color: '#gray-500',
            emoji: '❓'
          },
          states: states.map((state: any) => ({
            id: state.id,
            name: state.name,
            color: state.color,
            emoji: state.emoji
          })),
          users: members.map((member: any) => ({
            id: member.users.id,
            name: member.users.name,
            email: member.users.email,
            role: member.role as RoomMemberRole,
            avatar: member.users.avatar
          })),
          autoResetState: autoResetState ? {
            stateId: autoResetState.id,
            afterMinutes: room.auto_reset_minutes || 0
          } : undefined,
          broadcastLinks: links.map((link: any) => ({
            id: link.id,
            name: link.name,
            active: link.active,
            expiresAt: link.expires_at ? new Date(link.expires_at).getTime() : undefined,
            timeoutMinutes: link.timeout_minutes,
            createdAt: new Date(link.created_at).getTime()
          })),
          lastUpdated: new Date(room.updated_at).getTime(),
          lastUpdatedBy: room.created_by // This is a simplification
        };
      });

      return rooms;
    } catch (error) {
      console.error('Error in getUserRooms:', error);
      throw error;
    }
  }

  static async refreshRoomUserProfiles(roomId: string): Promise<RoomMember[]> {
    try {
      const { data: roomMembers, error } = await supabase
        .from('room_members')
        .select(`
          user_id,
          role,
          joined_at,
          users!inner (
            id,
            name,
            email,
            avatar
          )
        `)
        .eq('room_id', roomId);

      if (error) {
        console.error('Error refreshing room user profiles:', error);
        throw error;
      }

      return roomMembers?.map((member: any) => ({
        id: member.users.id,
        name: member.users.name,
        email: member.users.email,
        role: member.role as RoomMemberRole,
        avatar: member.users.avatar
      })) || [];
    } catch (error) {
      console.error('Error in refreshRoomUserProfiles:', error);
      throw error;
    }
  }

  static async createRoom(roomData: Omit<Room, 'id' | 'lastUpdated' | 'lastUpdatedBy' | 'broadcastLinks'>, createdBy: string): Promise<Room> {
    try {
      // Create the room
      const { data: room, error: roomError } = await supabase
        .from('rooms')
        .insert({
          name: roomData.name,
          size: roomData.size,
          view_mode: roomData.viewMode,
          created_by: createdBy
        })
        .select()
        .single();

      if (roomError) {
        console.error('Error creating room:', roomError);
        throw roomError;
      }

      // Create room states
      const statesWithRoomId = roomData.states.map(state => ({
        room_id: room.id,
        name: state.name,
        color: state.color,
        emoji: state.emoji
      }));

      const { data: createdStates, error: statesError } = await supabase
        .from('room_states')
        .insert(statesWithRoomId)
        .select();

      if (statesError) {
        console.error('Error creating room states:', statesError);
        throw statesError;
      }

      // Set the current state to the first created state
      const firstStateId = createdStates?.[0]?.id;
      if (firstStateId) {
        const { error: updateError } = await supabase
          .from('rooms')
          .update({ current_state_id: firstStateId })
          .eq('id', room.id);

        if (updateError) {
          console.error('Error setting current state:', updateError);
          throw updateError;
        }
      }

      // Add the creator as an admin member
      const { error: memberError } = await supabase
        .from('room_members')
        .insert({
          room_id: room.id,
          user_id: createdBy,
          role: 'ADMIN'
        });

      if (memberError) {
        console.error('Error adding creator as member:', memberError);
        throw memberError;
      }

      // Get the creator's user details
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, name, email, avatar')
        .eq('id', createdBy)
        .single();

      if (userError) {
        console.error('Error fetching user data:', userError);
        throw userError;
      }

      return {
        id: room.id,
        name: room.name,
        size: room.size as 'small' | 'medium' | 'large',
        viewMode: room.view_mode as 'minimal' | 'detailed',
        currentState: createdStates?.[0] ? {
          id: createdStates[0].id,
          name: createdStates[0].name,
          color: createdStates[0].color,
          emoji: createdStates[0].emoji
        } : roomData.currentState,
        states: createdStates?.map((state: any) => ({
          id: state.id,
          name: state.name,
          color: state.color,
          emoji: state.emoji
        })) || [],
        users: [{
          id: userData.id,
          name: userData.name,
          email: userData.email,
          role: 'ADMIN' as RoomMemberRole,
          avatar: userData.avatar
        }],
        autoResetState: roomData.autoResetState,
        broadcastLinks: [],
        lastUpdated: new Date(room.created_at).getTime(),
        lastUpdatedBy: createdBy
      };
    } catch (error) {
      console.error('Error in createRoom:', error);
      throw error;
    }
  }

  static async updateRoomState(roomId: string, stateId: string, userId: string): Promise<void> {
    try {
      // Update the room's current state
      const { error: roomError } = await supabase
        .from('rooms')
        .update({ current_state_id: stateId })
        .eq('id', roomId);

      if (roomError) {
        console.error('Error updating room state:', roomError);
        throw roomError;
      }

      // Log the state change in history
      const { error: historyError } = await supabase
        .from('room_state_history')
        .insert({
          room_id: roomId,
          state_id: stateId,
          changed_by: userId
        });

      if (historyError) {
        console.error('Error logging state history:', historyError);
        throw historyError;
      }
    } catch (error) {
      console.error('Error in updateRoomState:', error);
      throw error;
    }
  }

  static async inviteUserToRoom(roomId: string, email: string, role: RoomMemberRole = 'USER', message?: string): Promise<RoomInvitation> {
    try {
      const { data: invitation, error } = await supabase
        .from('room_invitations')
        .insert({
          room_id: roomId,
          inviter_id: (await supabase.auth.getUser()).data.user?.id,
          invitee_email: email,
          role: role,
          message: message
        })
        .select(`
          *,
          rooms!inner (name),
          users!room_invitations_inviter_id_fkey (name)
        `)
        .single();

      if (error) {
        console.error('Error creating room invitation:', error);
        throw error;
      }

      return {
        id: invitation.id,
        roomId: invitation.room_id,
        roomName: invitation.rooms.name,
        inviterName: invitation.users.name,
        inviteeEmail: invitation.invitee_email,
        role: invitation.role as RoomMemberRole,
        status: invitation.status as 'pending' | 'accepted' | 'declined' | 'expired',
        message: invitation.message,
        expiresAt: new Date(invitation.expires_at),
        createdAt: new Date(invitation.created_at)
      };
    } catch (error) {
      console.error('Error in inviteUserToRoom:', error);
      throw error;
    }
  }

  static async getUserInvitations(userId: string): Promise<RoomInvitation[]> {
    try {
      const { data: user } = await supabase
        .from('users')
        .select('email')
        .eq('id', userId)
        .single();

      if (!user) throw new Error('User not found');

      const { data: invitations, error } = await supabase
        .from('room_invitations')
        .select(`
          *,
          rooms!inner (name),
          users!room_invitations_inviter_id_fkey (name)
        `)
        .eq('invitee_email', user.email)
        .eq('status', 'pending')
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching user invitations:', error);
        throw error;
      }

      return invitations?.map((invitation: any) => ({
        id: invitation.id,
        roomId: invitation.room_id,
        roomName: invitation.rooms.name,
        inviterName: "Anon",
        inviteeEmail: invitation.invitee_email,
        role: invitation.role as RoomMemberRole,
        status: invitation.status as 'pending' | 'accepted' | 'declined' | 'expired',
        message: invitation.message,
        expiresAt: new Date(invitation.expires_at),
        createdAt: new Date(invitation.created_at)
      })) || [];
    } catch (error) {
      console.error('Error in getUserInvitations:', error);
      throw error;
    }
  }

  static async acceptInvitation(invitationId: string): Promise<void> {
    try {
      const { data, error } = await supabase.rpc('accept_room_invitation', {
        invitation_id: invitationId
      });

      if (error) {
        console.error('Error accepting invitation:', error);
        throw error;
      }

      if (!data) {
        throw new Error('Failed to accept invitation');
      }
    } catch (error) {
      console.error('Error in acceptInvitation:', error);
      throw error;
    }
  }

  static async declineInvitation(invitationId: string): Promise<void> {
    try {
      const { data, error } = await supabase.rpc('decline_room_invitation', {
        invitation_id: invitationId
      });

      if (error) {
        console.error('Error declining invitation:', error);
        throw error;
      }

      if (!data) {
        throw new Error('Failed to decline invitation');
      }
    } catch (error) {
      console.error('Error in declineInvitation:', error);
      throw error;
    }
  }

  static async addUserToRoom(roomId: string, userId: string, role: RoomMemberRole = 'USER'): Promise<void> {
    try {
      const { error } = await supabase
        .from('room_members')
        .insert({
          room_id: roomId,
          user_id: userId,
          role: role
        });

      if (error) {
        console.error('Error adding user to room:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in addUserToRoom:', error);
      throw error;
    }
  }

  static async removeUserFromRoom(roomId: string, userId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('room_members')
        .delete()
        .eq('room_id', roomId)
        .eq('user_id', userId);

      if (error) {
        console.error('Error removing user from room:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in removeUserFromRoom:', error);
      throw error;
    }
  }

  static async updateUserRole(roomId: string, userId: string, role: RoomMemberRole): Promise<void> {
    try {
      const { error } = await supabase
        .from('room_members')
        .update({ role })
        .eq('room_id', roomId)
        .eq('user_id', userId);

      if (error) {
        console.error('Error updating user role:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in updateUserRole:', error);
      throw error;
    }
  }

  static async updateRoomSettings(roomId: string, settings: Partial<Pick<Room, 'size' | 'viewMode'>>): Promise<void> {
    try {
      const updateData: any = {};
      if (settings.size) updateData.size = settings.size;
      if (settings.viewMode) updateData.view_mode = settings.viewMode;

      const { error } = await supabase
        .from('rooms')
        .update(updateData)
        .eq('id', roomId);

      if (error) {
        console.error('Error updating room settings:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in updateRoomSettings:', error);
      throw error;
    }
  }

  static async updateRoomTimeoutSettings(roomId: string, settings: { autoResetState?: { stateId: string; afterMinutes: number } }): Promise<void> {
    try {
      const updateData: any = {};
      
      if (settings.autoResetState) {
        updateData.auto_reset_state_id = settings.autoResetState.stateId;
        updateData.auto_reset_minutes = settings.autoResetState.afterMinutes;
      } else {
        updateData.auto_reset_state_id = null;
        updateData.auto_reset_minutes = null;
      }

      const { error } = await supabase
        .from('rooms')
        .update(updateData)
        .eq('id', roomId);

      if (error) {
        console.error('Error updating room timeout settings:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in updateRoomTimeoutSettings:', error);
      throw error;
    }
  }

  static async updateRoomStates(roomId: string, states: RoomState[]): Promise<void> {
    try {
      // Get existing states for this room
      const { data: existingStates, error: fetchError } = await supabase
        .from('room_states')
        .select('id')
        .eq('room_id', roomId);

      if (fetchError) {
        console.error('Error fetching existing states:', fetchError);
        throw fetchError;
      }

      const existingStateIds = existingStates?.map((s: any) => s.id) || [];
      
      // Separate new states from existing ones
      const newStates = states.filter(state => !state.id || state.id.startsWith('temp_'));
      const existingStatesToUpdate = states.filter(state => state.id && !state.id.startsWith('temp_'));

      // Insert new states
      if (newStates.length > 0) {
        const newStatesData = newStates.map(state => ({
          room_id: roomId,
          name: state.name,
          color: state.color,
          emoji: state.emoji
        }));

        const { error: insertError } = await supabase
          .from('room_states')
          .insert(newStatesData);

        if (insertError) {
          console.error('Error inserting new room states:', insertError);
          throw insertError;
        }
      }

      // Update existing states
      for (const state of existingStatesToUpdate) {
        const { error: updateError } = await supabase
          .from('room_states')
          .update({
            name: state.name,
            color: state.color,
            emoji: state.emoji
          })
          .eq('id', state.id);

        if (updateError) {
          console.error('Error updating room state:', updateError);
          throw updateError;
        }
      }

      // Find states to delete (existed before but not in new list)
      const currentStateIds = existingStatesToUpdate.map(s => s.id);
      const statesToDelete = existingStateIds.filter((id: string) => !currentStateIds.includes(id));

      // Only delete states that are not referenced in history
      if (statesToDelete.length > 0) {
        // Check which states are referenced in history
        const { data: referencedStates, error: historyError } = await supabase
          .from('room_state_history')
          .select('state_id')
          .in('state_id', statesToDelete);

        if (historyError) {
          console.error('Error checking state history references:', historyError);
          throw historyError;
        }

        const referencedStateIds = referencedStates?.map((h: any) => h.state_id) || [];
        const safeToDelete = statesToDelete.filter(id => !referencedStateIds.includes(id));

        // Delete only states that are safe to delete
        if (safeToDelete.length > 0) {
          const { error: deleteError } = await supabase
            .from('room_states')
            .delete()
            .in('id', safeToDelete);

          if (deleteError) {
            console.error('Error deleting unused room states:', deleteError);
            throw deleteError;
          }
        }
      }

    } catch (error) {
      console.error('Error in updateRoomStates:', error);
      throw error;
    }
  }

  static async createBroadcastLink(roomId: string, options: { name?: string; timeoutMinutes?: number; expiresAt?: Date } = {}): Promise<BroadcastLink> {
    try {
      const { data, error } = await supabase
        .from('broadcast_links')
        .insert({
          room_id: roomId,
          name: options.name || 'Broadcast Link',
          timeout_minutes: options.timeoutMinutes,
          expires_at: options.expiresAt?.toISOString()
        })
        .select()
        .single();

      if (error) {
        console.error('Error creating broadcast link:', error);
        throw error;
      }

      return {
        id: data.id,
        name: data.name,
        active: data.active,
        expiresAt: data.expires_at ? new Date(data.expires_at).getTime() : undefined,
        timeoutMinutes: data.timeout_minutes,
        createdAt: new Date(data.created_at).getTime()
      };
    } catch (error) {
      console.error('Error in createBroadcastLink:', error);
      throw error;
    }
  }

  static async updateBroadcastLink(roomId: string, linkId: string, updates: { name?: string; active?: boolean; timeoutMinutes?: number | null; expiresAt?: Date | null }): Promise<void> {
    try {
      const updateData: any = {};
      if (updates.name !== undefined) updateData.name = updates.name;
      if (updates.active !== undefined) updateData.active = updates.active;
      if (updates.timeoutMinutes !== undefined) updateData.timeout_minutes = updates.timeoutMinutes;
      if (updates.expiresAt !== undefined) updateData.expires_at = updates.expiresAt?.toISOString() || null;

      const { error } = await supabase
        .from('broadcast_links')
        .update(updateData)
        .eq('id', linkId)
        .eq('room_id', roomId);

      if (error) {
        console.error('Error updating broadcast link:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in updateBroadcastLink:', error);
      throw error;
    }
  }

  static async revokeBroadcastLink(roomId: string, linkId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('broadcast_links')
        .update({ active: false })
        .eq('id', linkId)
        .eq('room_id', roomId);

      if (error) {
        console.error('Error revoking broadcast link:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in revokeBroadcastLink:', error);
      throw error;
    }
  }

  static async deleteBroadcastLink(roomId: string, linkId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('broadcast_links')
        .delete()
        .eq('id', linkId)
        .eq('room_id', roomId);

      if (error) {
        console.error('Error deleting broadcast link:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in deleteBroadcastLink:', error);
      throw error;
    }
  }

  static async getRoomByBroadcastLink(roomId: string, linkId: string): Promise<Room | null> {
    try {
      // First verify the broadcast link exists and is active
      const { data: link, error: linkError } = await supabase
        .from('broadcast_links')
        .select('*')
        .eq('id', linkId)
        .eq('room_id', roomId)
        .eq('active', true)
        .single();

      if (linkError || !link) {
        console.error('Broadcast link not found or inactive:', linkError);
        return null;
      }

      // Check if link has expired
      if (link.expires_at && new Date(link.expires_at) < new Date()) {
        console.error('Broadcast link has expired');
        return null;
      }

      // Get the room data
      const { data: room, error: roomError } = await supabase
        .from('rooms')
        .select(`
          id,
          name,
          size,
          view_mode,
          current_state_id,
          auto_reset_state_id,
          auto_reset_minutes,
          created_by,
          created_at,
          updated_at
        `)
        .eq('id', roomId)
        .single();

      if (roomError || !room) {
        console.error('Room not found:', roomError);
        return null;
      }

      // Get room states
      const { data: roomStates, error: statesError } = await supabase
        .from('room_states')
        .select('*')
        .eq('room_id', roomId);

      if (statesError) {
        console.error('Error fetching room states:', statesError);
        throw statesError;
      }

      const states = roomStates?.map((state: any) => ({
        id: state.id,
        name: state.name,
        color: state.color,
        emoji: state.emoji
      })) || [];

      const currentState = states.find(state => state.id === room.current_state_id) || states[0];
      const autoResetState = room.auto_reset_state_id 
        ? states.find(state => state.id === room.auto_reset_state_id)
        : undefined;

      return {
        id: room.id,
        name: room.name,
        size: room.size as 'small' | 'medium' | 'large',
        viewMode: room.view_mode as 'minimal' | 'detailed',
        currentState: currentState || {
          id: 'default',
          name: 'Unknown',
          color: '#gray-500',
          emoji: '❓'
        },
        states,
        users: [], // Broadcast view doesn't need user details
        autoResetState: autoResetState ? {
          stateId: autoResetState.id,
          afterMinutes: room.auto_reset_minutes || 0
        } : undefined,
        broadcastLinks: [], // Not needed for broadcast view
        lastUpdated: new Date(room.updated_at).getTime(),
        lastUpdatedBy: room.created_by
      };
    } catch (error) {
      console.error('Error in getRoomByBroadcastLink:', error);
      throw error;
    }
  }
}